import Vue from "vue";
import Vuex from "vuex";
import VuexPersist from "vuex-persist";
import { connectSocket, disconnectSocket } from "@/socket";
import { upload } from "./modules/upload";
import { download } from "./modules/download";

Vue.use(Vuex);

const vuexLocal = new VuexPersist({
  key: "admin-bv-default",
  storage: window.localStorage,
  modules: [
    "accessToken",
    "refreshToken",
    "user",
    "filters",
    "attendance",
    "pauseStart",
    "pauseEnd",
    "currentPauseBody",
    "pauseStartTimestamp",
    "currentAttendanceSelected",
  ],
});

export default new Vuex.Store({
  state: {
    accessToken: "",
    refreshToken: "",
    deviceToken: "",
    user: null,
    attendance: null,
    audience: null,
    filters: {
      groups: [],
      subgroups: [],
      desks: [],
      attendants: [],
    },
    pauseStart: false,
    pauseEnd: false,
    currentPauseBody: null,
    pauseStartTimestamp: null,
    currentAttendanceSelected: null,
    socketSession: null, // Dados da sessão do socket
    linkedAccess: true,
    attendanceAccess: true,
    audienceAccess: true,
  },

  getters: {
    authenticated: ({ accessToken, refreshToken, user }) =>
      !!accessToken && !!refreshToken && !!user,
    accessToken: ({ accessToken }) => accessToken,
    refreshToken: ({ refreshToken }) => refreshToken,
    deviceToken: ({ deviceToken }) => deviceToken,
    user: ({ user }) => user,
    pauseStart: ({ pauseStart }) => pauseStart,
    pauseEnd: ({ pauseEnd }) => pauseEnd,
    pauseStartTimestamp: ({ pauseStartTimestamp }) => pauseStartTimestamp,
    currentPauseBody: ({ currentPauseBody }) => currentPauseBody,
    attendance: ({ attendance }) => attendance,
    currentAttendanceSelected: (state) => state.currentAttendanceSelected,
    audience: ({ audience }) => audience,
    isMaster: ({ user }) =>
      user?.permissionGroups?.some((p) => p.name.toLowerCase() === "master"),
    attendanceRole: ({ user }) => {
      // operator (attendant), moderator (coordinator), admin (director)

      if (!user?.permissionGroups) return null;

      const result = user.permissionGroups.find(
        (group) => !!group.role && group.module === "attendances"
      );
      return result ? result.role : null;
    },
    audienceRole: ({ user }) => {
      if (!user?.permissionGroups) return null;

      const result = user.permissionGroups.find(
        (group) => !!group.role && group.module === "audiences"
      );
      return result ? result.role : null;
    },
    masterPermission: ({ user }) => {
      if (!user?.permissionGroups?.length) return null;

      return !!user.permissionGroups.find((el) => el.name === "Master");
    },
    linkedAccess: ({ linkedAccess }) => linkedAccess,
    attendanceAccess: ({ attendanceAccess }) => attendanceAccess,
    audienceAccess: ({ audienceAccess }) => audienceAccess,
  },

  mutations: {
    SET_ACCESS_TOKEN(state, payload) {
      state.accessToken = payload;
    },
    SET_CURRENT_PAUSE_BODY(state, payload) {
      state.currentPauseBody = payload;
    },
    SET_DEVICE_TOKEN(state, payload) {
      state.deviceToken = payload;
    },
    SET_MULTI_ATTENDANCE(state, { id, data }) {
      Vue.set(state.multiAttendances, id, data);
    },
    REMOVE_MULTI_ATTENDANCE(state, id) {
      Vue.delete(state.multiAttendances, id);
    },
    SET_REFRESH_TOKEN(state, payload) {
      state.refreshToken = payload;
    },
    SET_ACCOUNT(state, payload) {
      state.user = payload;
    },
    SET_FILTERS_GROUPS(state, payload) {
      state.filters.groups = payload;
    },
    SET_FILTERS_SUBGROUPS(state, payload) {
      state.filters.subgroups = payload;
    },
    SET_FILTERS_DESKS(state, payload) {
      state.filters.desks = payload;
    },
    SET_FILTERS_ATTENDANTS(state, payload) {
      state.filters.attendants = payload;
    },
    SET_ATTENDANCE(state, payload) {
      state.attendance = payload;
    },
    SET_CURRENT_ATTENDANCE_SELECTED(state, payload) {
      state.currentAttendanceSelected = payload;
    },
    SET_AUDIENCE(state, payload) {
      state.audience = payload;
    },
    SET_PAUSE_TIMES(state, payload) {
      state.pauseStart = payload?.start || false;
      state.pauseEnd = payload?.end || false;

      // Se estamos iniciando uma pausa, armazenamos o timestamp atual
      if (payload?.start && !state?.pauseStartTimestamp) {
        state.pauseStartTimestamp = Date.now();
      }

      // Se estamos resetando a pausa, limpamos o timestamp
      if (!payload?.start && !payload?.end) {
        state.pauseStartTimestamp = null;
      }
    },

    SET_USER_STATUS(state, payload) {
      if (!state.user) return;

      state.user.status = payload.status;
    },
    SET_SOCKET_SESSION(state, data) {
      state.socketSession = data;
    },
    SET_LINKED_ACCESS(state, payload) {
      state.linkedAccess = payload;
    },
    SET_ATTENDANCE_ACCESS(state, payload) {
      state.attendanceAccess = payload;
    },

    SET_AUDIENCE_ACCESS(state, payload) {
      state.audienceAccess = payload;
    },
  },

  actions: {
    setAccessToken({ commit }, payload) {
      commit("SET_ACCESS_TOKEN", payload);
    },
    setRefreshToken({ commit }, payload) {
      commit("SET_REFRESH_TOKEN", payload);
    },
    setCurrentPauseBody({ commit }, payload) {
      commit("SET_CURRENT_PAUSE_BODY", payload);
    },
    setAdminStatus({ commit }, payload) {
      commit("SET_ADMIN_STATUS", payload);
    },
    setUserStatus({ commit }, payload) {
      commit("SET_USER_STATUS", payload);
    },
    setCurrentAttendanceSelected({ commit }, payload) {
      commit("SET_CURRENT_ATTENDANCE_SELECTED", payload);
    },
    setDeviceToken({ commit }, payload) {
      commit("SET_DEVICE_TOKEN", payload);
    },
    setUser({ commit }, payload) {
      commit("SET_ACCOUNT", payload);
    },
    setPauseTimes({ commit }, payload) {
      commit("SET_PAUSE_TIMES", payload);
    },
    setSignIn({ dispatch }, payload) {
      dispatch("setAccessToken", payload.accessToken);
      dispatch("setRefreshToken", payload.refreshToken);
      dispatch("setDeviceToken", payload.deviceToken);
      dispatch("setUser", payload.user);
      dispatch("setPauseTimes", payload.pauseTimes);
      dispatch("setCurrentPauseBody", payload.currentPauseBody);
      dispatch(
        "setCurrentAttendanceSelected",
        payload.currentAttendanceSelected
      );

      connectSocket();
    },
    setSignOut({ dispatch }) {
      dispatch("setAccessToken", "");
      dispatch("setRefreshToken", "");
      dispatch("setUser", null);
      dispatch("setPauseTimes", { start: "", end: "" });
      dispatch("setFiltersGroups", []);
      dispatch("setFiltersSubgroups", []);
      dispatch("setFiltersDesks", []);
      dispatch("setFiltersAttendants", []);
      dispatch("clearFilters");
      dispatch("setCurrentAttendanceSelected", null);
      dispatch("setLinkedAccess", true);

      disconnectSocket();
    },
    setFiltersGroups({ commit }, payload) {
      commit("SET_FILTERS_GROUPS", payload);
    },
    setFiltersSubgroups({ commit }, payload) {
      commit("SET_FILTERS_SUBGROUPS", payload);
    },
    setFiltersDesks({ commit }, payload) {
      commit("SET_FILTERS_DESKS", payload);
    },
    setFiltersAttendants({ commit }, payload) {
      commit("SET_FILTERS_ATTENDANTS", payload);
    },
    setAttendance({ commit }, payload) {
      commit("SET_ATTENDANCE", payload);
    },
    setAudience({ commit }, payload) {
      commit("SET_AUDIENCE", payload);
    },
    clearFilters({ dispatch }) {
      dispatch("setFiltersGroups", []);
      dispatch("setFiltersSubgroups", []);
      dispatch("setFiltersDesks", []);
      dispatch("setFiltersAttendants", []);
    },
    setSocketSession({ commit }, payload) {
      commit("SET_SOCKET_SESSION", payload);
    },
    setLinkedAccess({ commit }, payload) {
      commit("SET_LINKED_ACCESS", payload);
    },
    setAttendanceAccess({ commit }, payload) {
      commit("SET_ATTENDANCE_ACCESS", payload);
    },
    setAudienceAccess({ commit }, payload) {
      commit("SET_AUDIENCE_ACCESS", payload);
    },
  },

  plugins: [vuexLocal.plugin],

  modules: {
    upload,
    download,
  },
});
